import { useRef } from 'react'

import { Textarea as ChakraTextarea, TextareaProps } from '@chakra-ui/react'
import styled from '@emotion/styled'

const StyledTextarea = styled(ChakraTextarea)`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.47px;
  line-height: 21px;
  color: #2d2e41;

  ::placeholder {
    font-weight: 500;
    color: #2d2e41;
    opacity: 30%;
    font-style: italic;
  }
`

export interface TextareaIProps extends TextareaProps {
  /**
   * Textarea id.
   */
  id?: string
  /**
   * Textarea name.
   */
  name?: string
  /**
   * If `true`, the Textarea will display valid state styles with check icon.
   */
  valid?: boolean
  /**
   * If `true`, the Textarea will display error state styles.
   */
  isInvalid?: boolean
  /**
   * The placeholder value for this Textarea.
   */
  placeholder?: string
  /**
   * The width of the Textarea element.
   */
  width?: string
  /**
   * If `true`, automatically capitalizes the first letter of each sentence.
   */
  autoCapitalizeSentences?: boolean
  /**
   * Reference to the textarea element.
   */
  textareaRef?:
    | React.RefObject<HTMLTextAreaElement>
    | ((instance: HTMLTextAreaElement | null) => void)
  [key: string]: unknown
}

// Regex pattern to match sentence endings (. ? !) followed by space and a lowercase letter
const SENTENCE_BOUNDARY_PATTERN = /([.!?]\s+)([a-z])/g

export const Textarea = ({
  name,
  id,
  isInvalid,
  placeholder,
  valid,
  textareaRef,
  width = '100%',
  autoCapitalizeSentences = true,
  onChange,
  ...rest
}: TextareaIProps) => {
  const internalRef = useRef<HTMLTextAreaElement>(null)

  // Auto-capitalize the first letter of each sentence
  const capitalizeSentences = (text: string): string => {
    if (!text) return text

    // Capitalize first character
    let result = text.charAt(0).toUpperCase() + text.slice(1)

    // Capitalize after sentence endings (. ? !) followed by space
    result = result.replace(
      SENTENCE_BOUNDARY_PATTERN,
      (_match, punctuation, letter) => {
        return punctuation + letter.toUpperCase()
      }
    )

    return result
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (autoCapitalizeSentences) {
      const cursorPosition = e.target.selectionStart
      const originalValue = e.target.value
      const capitalizedValue = capitalizeSentences(originalValue)

      // Update the value
      e.target.value = capitalizedValue

      // Call the original onChange handler
      if (onChange) {
        onChange(e)
      }

      // Restore cursor position after React updates
      setTimeout(() => {
        // Get the ref, preferring textareaRef if provided, otherwise use internal ref
        let element: HTMLTextAreaElement | null = null
        if (textareaRef && typeof textareaRef !== 'function') {
          element = textareaRef.current
        } else if (internalRef.current) {
          element = internalRef.current
        }

        if (element) {
          element.selectionStart = cursorPosition
          element.selectionEnd = cursorPosition
        }
      }, 0)
    } else {
      // Call the original onChange handler without modification
      if (onChange) {
        onChange(e)
      }
    }
  }

  return (
    <StyledTextarea
      autoComplete='off'
      autoCorrect='off'
      bgColor='white'
      borderColor={valid ? '#2BD2B4' : '#9FB8E5'}
      borderWidth='2px'
      data-testid='text-Textarea'
      errorBorderColor='#D01030'
      isInvalid={isInvalid}
      name={name || id}
      onChange={handleChange}
      placeholder={placeholder}
      ref={(textareaRef || internalRef) as React.RefObject<HTMLTextAreaElement>}
      spellCheck='true'
      variant='outline'
      width={width}
      {...rest}
    />
  )
}
